import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { opinionsApi } from '../api/opinions';
import { useToast } from '../contexts/ToastContext';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ChevronRightIcon,
  TagIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';

function OpinionsPage() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [selectedOpinion, setSelectedOpinion] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    topic: '',
    position: '',
    sentiment: 'neutral',
    keyPoints: [],
    keywords: [],
    examplePhrases: [],
    nuances: [],
  });

  // Sentiment options with descriptions for blurb tone distribution
  const SENTIMENT_OPTIONS = [
    { value: 'strongly-favor', label: 'Strongly Favor', description: '4 positive, 1 neutral, 1 negative blurbs' },
    { value: 'favor', label: 'Favor', description: '3 positive, 2 neutral, 1 negative blurbs' },
    { value: 'neutral', label: 'Neutral', description: '2 positive, 2 neutral, 2 negative blurbs' },
    { value: 'oppose', label: 'Oppose', description: '1 positive, 2 neutral, 3 negative blurbs' },
    { value: 'strongly-oppose', label: 'Strongly Oppose', description: '1 positive, 1 neutral, 4 negative blurbs' },
  ];

  const { data: opinionsData, isLoading } = useQuery({
    queryKey: ['opinions'],
    queryFn: () => opinionsApi.list({ limit: 100 }),
  });

  const createMutation = useMutation({
    mutationFn: opinionsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['opinions']);
      setIsEditing(false);
      setSelectedOpinion(null);
      showSuccess('Opinion created successfully');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Failed to create opinion');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }) => opinionsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['opinions']);
      setIsEditing(false);
      showSuccess('Opinion updated successfully');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Failed to update opinion');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: opinionsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['opinions']);
      setSelectedOpinion(null);
      showSuccess('Opinion deleted successfully');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Failed to delete opinion');
    },
  });

  const handleCreate = () => {
    setSelectedOpinion(null);
    setFormData({
      id: '',
      topic: '',
      position: '',
      sentiment: 'neutral',
      keyPoints: [{ point: '', strength: 'moderate' }],
      keywords: [''],
      examplePhrases: [''],
      nuances: [''],
    });
    setIsEditing(true);
  };

  const handleEdit = () => {
    setFormData({
      id: selectedOpinion.id,
      topic: selectedOpinion.topic,
      position: selectedOpinion.position,
      sentiment: selectedOpinion.sentiment || 'neutral',
      keyPoints: selectedOpinion.keyPoints || [{ point: '', strength: 'moderate' }],
      keywords: selectedOpinion.keywords || [''],
      examplePhrases: selectedOpinion.examplePhrases || [''],
      nuances: selectedOpinion.nuances || [''],
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    const cleanedData = {
      ...formData,
      id: formData.id || formData.topic.toLowerCase().replace(/\s+/g, '-'),
      sentiment: formData.sentiment || 'neutral',
      keyPoints: formData.keyPoints.filter((kp) => kp.point.trim()),
      keywords: formData.keywords.filter((k) => k.trim()),
      examplePhrases: formData.examplePhrases.filter((p) => p.trim()),
      nuances: formData.nuances.filter((n) => n.trim()),
    };

    if (selectedOpinion) {
      updateMutation.mutate(cleanedData);
    } else {
      createMutation.mutate(cleanedData);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this opinion?')) {
      deleteMutation.mutate(selectedOpinion.id);
    }
  };

  const addArrayItem = (field, defaultValue = '') => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], defaultValue],
    }));
  };

  const updateArrayItem = (field, index, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading opinions...</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-obn-blue dark:text-white">Opinions</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Manage topic-based opinions for context-aware AI generation
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-obn-blue hover:bg-obn-blue/90"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Opinion
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Opinion List */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 shadow dark:ring-1 dark:ring-white/10 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {opinionsData?.opinions?.length || 0} Topics
              </h3>
            </div>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
              {opinionsData?.opinions?.map((opinion) => (
                <li
                  key={opinion.id}
                  onClick={() => {
                    setSelectedOpinion(opinion);
                    setIsEditing(false);
                  }}
                  className={`px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between ${
                    selectedOpinion?.id === opinion.id ? 'bg-obn-lightblue dark:bg-blue-900/30' : ''
                  }`}
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{opinion.topic}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {opinion.keywords?.length || 0} keywords •{' '}
                      {opinion.keyPoints?.length || 0} key points
                    </div>
                  </div>
                  <ChevronRightIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                </li>
              ))}
              {(!opinionsData?.opinions || opinionsData.opinions.length === 0) && (
                <li className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                  No opinions yet. Add your first topic!
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Opinion Detail / Editor */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 shadow dark:ring-1 dark:ring-white/10 rounded-lg p-6">
            {!selectedOpinion && !isEditing ? (
              <div className="text-center py-12">
                <LightBulbIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">Select an opinion to view or edit</p>
              </div>
            ) : isEditing ? (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  {selectedOpinion ? 'Edit Opinion' : 'New Opinion'}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Topic</label>
                    <input
                      type="text"
                      value={formData.topic}
                      onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                      className="mt-1 block w-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                      placeholder="e.g., Cold Starts"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">ID (slug)</label>
                    <input
                      type="text"
                      value={formData.id}
                      onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                      className="mt-1 block w-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                      placeholder="auto-generated from topic"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Position</label>
                  <textarea
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                    placeholder="Summary of your position on this topic..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Sentiment</label>
                  <select
                    value={formData.sentiment}
                    onChange={(e) => setFormData({ ...formData, sentiment: e.target.value })}
                    className="mt-1 block w-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                  >
                    {SENTIMENT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {SENTIMENT_OPTIONS.find((o) => o.value === formData.sentiment)?.description}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Keywords</label>
                  {formData.keywords.map((keyword, idx) => (
                    <div key={idx} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={keyword}
                        onChange={(e) => updateArrayItem('keywords', idx, e.target.value)}
                        className="flex-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                        placeholder="Keyword for matching"
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayItem('keywords', idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('keywords', '')}
                    className="text-sm text-obn-blue dark:text-blue-400 hover:text-obn-blue/80 dark:hover:text-blue-300"
                  >
                    + Add keyword
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Key Points</label>
                  {formData.keyPoints.map((kp, idx) => (
                    <div key={idx} className="flex items-start space-x-2 mb-2">
                      <input
                        type="text"
                        value={kp.point}
                        onChange={(e) =>
                          updateArrayItem('keyPoints', idx, { ...kp, point: e.target.value })
                        }
                        className="flex-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                        placeholder="Key point"
                      />
                      <select
                        value={kp.strength}
                        onChange={(e) =>
                          updateArrayItem('keyPoints', idx, { ...kp, strength: e.target.value })
                        }
                        className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                      >
                        <option value="strong">Strong</option>
                        <option value="moderate">Moderate</option>
                        <option value="mild">Mild</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => removeArrayItem('keyPoints', idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('keyPoints', { point: '', strength: 'moderate' })}
                    className="text-sm text-obn-blue dark:text-blue-400 hover:text-obn-blue/80 dark:hover:text-blue-300"
                  >
                    + Add key point
                  </button>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-obn-blue hover:bg-obn-blue/90 disabled:opacity-50"
                  >
                    {selectedOpinion ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100">{selectedOpinion.topic}</h3>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">ID: {selectedOpinion.id}</div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleEdit}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={handleDelete}
                      className="inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-800/50 text-sm font-medium rounded-md text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/30"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Position</h4>
                  <p className="text-gray-600 dark:text-gray-300">{selectedOpinion.position}</p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Sentiment</h4>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      selectedOpinion.sentiment === 'strongly-favor'
                        ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200'
                        : selectedOpinion.sentiment === 'favor'
                          ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : selectedOpinion.sentiment === 'oppose'
                            ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                            : selectedOpinion.sentiment === 'strongly-oppose'
                              ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {SENTIMENT_OPTIONS.find((o) => o.value === (selectedOpinion.sentiment || 'neutral'))?.label || 'Neutral'}
                  </span>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {SENTIMENT_OPTIONS.find((o) => o.value === (selectedOpinion.sentiment || 'neutral'))?.description}
                  </p>
                </div>

                {selectedOpinion.keywords?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Keywords</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedOpinion.keywords.map((keyword, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200"
                        >
                          <TagIcon className="h-3 w-3 mr-1" />
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedOpinion.keyPoints?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Key Points</h4>
                    <ul className="space-y-2">
                      {selectedOpinion.keyPoints.map((kp, idx) => (
                        <li key={idx} className="flex items-start">
                          <span
                            className={`text-xs px-2 py-0.5 rounded mr-2 mt-0.5 ${
                              kp.strength === 'strong'
                                ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-200'
                                : kp.strength === 'moderate'
                                  ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-200'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                            }`}
                          >
                            {kp.strength}
                          </span>
                          <span className="text-gray-600 dark:text-gray-300">{kp.point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedOpinion.examplePhrases?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Example Phrases</h4>
                    <ul className="space-y-1">
                      {selectedOpinion.examplePhrases.map((phrase, idx) => (
                        <li key={idx} className="text-gray-600 dark:text-gray-300 italic">
                          "{phrase}"
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedOpinion.nuances?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Nuances</h4>
                    <ul className="space-y-1">
                      {selectedOpinion.nuances.map((nuance, idx) => (
                        <li key={idx} className="text-gray-600 dark:text-gray-300 text-sm">
                          • {nuance}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="text-xs text-gray-400 dark:text-gray-500 pt-4 border-t border-gray-200 dark:border-gray-700">
                  Created: {new Date(selectedOpinion.created).toLocaleString()} • Modified:{' '}
                  {new Date(selectedOpinion.modified).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OpinionsPage;
